import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import axios from 'axios';
import '../styles/SalesCharts.css';
import '../styles/AdminExpenseChart.css';

// Registrar componentes de Chart.js
ChartJS.register(ArcElement, Tooltip, Legend);

// Función para obtener el mes actual en español
const getCurrentMonth = () => {
  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  const currentMonth = new Date().getMonth();
  return months[currentMonth];
};

const SupervisorExpenseDoughnutChart = ({ selectedPOS }) => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const fetchExpenseData = async () => {
    try {
      console.log('SupervisorExpenseDoughnutChart: Cargando datos para POS:', selectedPOS);
      setLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No se encontró token de autenticación');
        setLoading(false);
        return;
      }
      
      // Construir URL con el filtro de punto de venta
      let url = '/api/supervisors/monthly-expense-categories';
      // Siempre incluir el parámetro pointOfSaleId, incluso si es 'all'
      url += `?pointOfSaleId=${selectedPOS || 'all'}`;
      
      console.log('SupervisorExpenseDoughnutChart: Consultando URL:', url);
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log('Datos de gastos recibidos:', response.data);
      console.log('Estructura completa:', JSON.stringify(response.data, null, 2));
      
      if (response.data && 
          Array.isArray(response.data.labels) && 
          Array.isArray(response.data.totals) && 
          response.data.labels.length > 0 && 
          response.data.totals.length > 0) {
        
        setChartData({
          labels: response.data.labels,
          datasets: [
            {
              data: response.data.totals,
              backgroundColor: [
                '#FF6384', // Rosa
                '#36A2EB', // Azul
                '#FFCE56', // Amarillo
                '#4BC0C0', // Verde agua
                '#9966FF', // Morado
                '#FF9F40'  // Naranja
              ],
              hoverBackgroundColor: [
                '#FF4394', // Rosa más oscuro
                '#36A2EB', // Azul
                '#FFCE56', // Amarillo
                '#4BC0C0', // Verde agua
                '#9966FF', // Morado
                '#FF9F40'  // Naranja
              ]
            }
          ]
        });
      } else {
        // Si no hay datos o están vacíos, establecer chartData a null
        setChartData(null);
      }
    } catch (err) {
      console.error('Error al cargar datos de gastos:', err);
      // En caso de error, también establecer chartData a null
      setChartData(null);
    } finally {
      setLoading(false);
    }
  };
  
  // Efecto para cargar datos cuando cambia selectedPOS
  useEffect(() => {
    console.log('SupervisorExpenseDoughnutChart: Efecto activado por cambio en selectedPOS:', selectedPOS);
    fetchExpenseData();
  }, [selectedPOS]);

  if (loading) {
    return <div className="charts-loading">Cargando gráfica de gastos...</div>;
  }
  
  // Si no hay datos de gastos, mostrar el mensaje "Sin gastos registrados"
  if (!chartData) {
    return (
      <div className="chart-container">
        <div className="charts-empty">Sin gastos registrados</div>
      </div>
    );
  }
  
  // Título de la gráfica con el mes actual y el punto de venta seleccionado
  const chartTitle = `Gastos de ${getCurrentMonth()} ${selectedPOS !== 'all' ? '(POS específico)' : '(Todos los POS)'}`;
  
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        align: 'center',
        labels: {
          boxWidth: 12,
          padding: 10,
          font: {
            size: 11
          }
        },
        // Ajustar márgenes de la leyenda
        maxHeight: 60,
        maxWidth: '100%'
      },
      title: {
        display: true,
        text: chartTitle,
        font: {
          size: 14
        },
        padding: {
          top: 5,
          bottom: 10
        }
      },
      // Deshabilitar tooltips en dispositivos móviles para evitar problemas de espacio
      tooltip: {
        enabled: window.innerWidth > 768,
        callbacks: {
          label: function(context) {
            const value = context.parsed;
            const total = context.dataset.data.reduce((sum, data) => sum + data, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `$${value.toLocaleString()} (${percentage}%)`;
          }
        }
      }
    },
    // Ajustar el tamaño del donut
    cutout: '60%',
    // Reducir el radio para dejar más espacio para la leyenda
    radius: '80%'
  };
  
  return (
    <div className="chart-container">
      <Doughnut data={chartData} options={options} />
    </div>
  );
};

export default SupervisorExpenseDoughnutChart;
